import { Box } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { WrapperCardWithTitle } from '../../layouts/WrapperCardWithTitle';
import { CustomSwitch } from '../Common/CustomSwitch';
import { useState } from 'react';
import BeltNoDataAvailable from './BeltNoDataAvailable';
import { useTranslation } from 'react-i18next';
import { sequentialColors } from '../../utils/chartColors';
import { formatTime } from '../../utils';

export interface BeltTimelineSeriesConfig {
  key: string;
  values: number[];
}

export interface BeltTimelineChartConfig {
  category: string;
  xAxisLabels: Date[];
  series: BeltTimelineSeriesConfig[];
}

interface BeltTimelineProps {
  chartConfigs: BeltTimelineChartConfig[];
}

type BeltTimelineMode = 'cumulative' | 'individual';

export default function BeltTimeline({ chartConfigs }: BeltTimelineProps) {
  const { t } = useTranslation();

  const [mode, setMode] = useState<BeltTimelineMode>('cumulative');

  return (
    <WrapperCardWithTitle
      title={t('belt.timeline')}
      sx={{ height: 'auto', flexGrow: 1, flexShrink: 0, flexBasis: 'auto' }}
      contentSx={{ gap: 2, paddingRight: '2rem' }}
      adornment={
        chartConfigs.length !== 0 && (
          <Box sx={{ marginLeft: 'auto' }}>
            <CustomSwitch
              options={[
                { value: 'cumulative', labelKey: 'belt.timelineView.cumulative' },
                { value: 'individual', labelKey: 'belt.timelineView.individual' },
              ]}
              value={mode}
              onToggle={setMode}
            />
          </Box>
        )
      }
    >
      {chartConfigs.length === 0 ? (
        <BeltNoDataAvailable />
      ) : (
        chartConfigs.map((config) => {
          const seriesColors = sequentialColors(config.series.length);
          const maxDataValue = Math.max(...config.series.flatMap((s) => s.values), 0);
          const calculatedMax = Math.min(maxDataValue * 1.1, 1);
          // cap axis at 1 since values are percentages and can never exceed it,
          // but scale to the max stacked value instead to avoid a lot of empty space
          const stackedTotals = config.xAxisLabels.map((_, i) =>
            config.series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0)
          );
          const maxStackedTotal = Math.max(...stackedTotals, 0);
          const calculatedBarMax = Math.max(1, maxStackedTotal * 1.05);
          return (
            <WrapperCardWithTitle
              key={config.category}
              title={`belt.categories.${config.category}`}
              titleSx={{ fontSize: '0.95rem', fontWeight: 500, color: 'text.secondary' }}
              elevation={0}
              sx={{ width: '100%', height: 500 }}
            >
              {mode === 'cumulative' ? (
                <BarChart
                  xAxis={[
                    {
                      scaleType: 'band',
                      data: config.xAxisLabels,
                      label: t('common.time'),
                      tickLabelStyle: { angle: 45 },
                      height: 80,
                      valueFormatter: (value) => formatTime(value),
                    },
                  ]}
                  yAxis={[{ max: calculatedBarMax }]}
                  series={config.series.map((s, idx) => ({
                    label: s.key,
                    data: s.values,
                    color: seriesColors[idx],
                    stack: 'total',
                  }))}
                />
              ) : (
                <LineChart
                  xAxis={[
                    {
                      scaleType: 'time',
                      data: config.xAxisLabels,
                      label: t('common.time'),
                      tickLabelStyle: { angle: 45 },
                      height: 80,
                      valueFormatter: (value) => formatTime(value),
                    },
                  ]}
                  yAxis={[{ max: calculatedMax }]}
                  margin={{ right: 50 }}
                  series={config.series.map((s, idx) => ({
                    label: s.key,
                    data: s.values,
                    color: seriesColors[idx],
                  }))}
                />
              )}
            </WrapperCardWithTitle>
          );
        })
      )}
    </WrapperCardWithTitle>
  );
}
